import React, { useState, useEffect, useMemo } from 'react'
import HostNav from '../components/hostComponents/HostNav'
import { useNavigate } from 'react-router-dom'
import { getAllOrders } from '../store/thunks/hostActions'
import { useDispatch, useSelector } from 'react-redux'
import { CircularProgress } from '@mui/material'
import { Link } from 'react-router-dom'

// ─── Compact Table Row ───────────────────────────────────────────────────────
function OrderTableRow({ order, index }) {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-gradient-to-r from-green-500 to-emerald-500 text-white';
      case 'picked':
        return 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white';
      case 'pending':
      case 'requested':
        return 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white';
      case 'cancelled':
        return 'bg-gradient-to-r from-red-500 to-rose-500 text-white';
      default:
        return 'bg-gradient-to-r from-gray-500 to-gray-600 text-white';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <tr className="group hover:bg-white/60 transition-all duration-200 border-b border-white/10">
      <td className="px-6 py-4">
        <div className="flex items-center space-x-3">
          <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          </div>
          <span className="font-semibold text-gray-900 text-sm">
            #{order.uniqueId || order._id?.slice(-8) || `ORD-${index + 1}`}
          </span>
        </div>
      </td>
      <td className="px-6 py-4">
        <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
          {order.property?.listingTitle || 'Property'}
        </p>
        <p className="text-xs text-gray-500 truncate max-w-[200px]">
          {order.property?.address?.city || 'N/A'}
        </p>
      </td>
      <td className="px-6 py-4">
        <span className="text-sm text-gray-700">{formatDate(order.createdAt || order.inStockDate)}</span>
      </td>
      <td className="px-6 py-4">
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-sm ${getStatusColor(order.status)}`}>
          {order.status === 'picked' ? 'Picked Up' : (order.status || 'Unknown')}
        </span>
      </td>
      <td className="px-6 py-4">
        <span className="text-sm font-bold text-green-600">${order.hostFee?.toFixed(2) || '0.00'}</span>
      </td>
      <td className="px-6 py-4 text-right">
        <span className="text-xs text-gray-500">{order.items?.length || 0} items</span>
      </td>
    </tr>
  );
}

// ─── Premium Order Card ──────────────────────────────────────────────────────
function OrderCard({ order, index }) {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-green-500/30';
      case 'picked':
        return 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-blue-500/30';
      case 'pending':
      case 'requested':
        return 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white shadow-amber-500/30';
      case 'cancelled':
        return 'bg-gradient-to-r from-red-500 to-rose-500 text-white shadow-red-500/30';
      default:
        return 'bg-gradient-to-r from-gray-500 to-gray-600 text-white shadow-gray-500/30';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'picked':
        return (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
        );
      case 'pending':
      case 'requested':
        return (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'cancelled':
        return (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    const parts = [address.line1, address.line2, address.city, address.state].filter(Boolean);
    return parts.join(', ');
  };

  return (
    <div className="group bg-white/70 backdrop-blur-xl rounded-2xl shadow-sm hover:shadow-xl border border-white/40 overflow-hidden transition-all duration-300 hover:-translate-y-1">
      {/* Order Header */}
      <div className="p-5 border-b border-gray-100/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/25">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">
                Order #{order.uniqueId || order._id?.slice(-8) || `ORD-${index + 1}`}
              </h3>
              <p className="text-xs text-gray-500">
                {formatDate(order.createdAt || order.inStockDate)}
              </p>
            </div>
          </div>
          <div className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-xs font-bold shadow-md ${getStatusColor(order.status)}`}>
            {getStatusIcon(order.status)}
            <span className="capitalize">
              {order.status === 'picked' ? 'Picked Up' : order.status}
            </span>
          </div>
        </div>
      </div>

      {/* Order Details */}
      <div className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left Column */}
          <div className="space-y-3">
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Property</h4>
              <div className="bg-gradient-to-br from-gray-50/80 to-gray-100/60 backdrop-blur-sm rounded-xl p-3 border border-gray-200/50">
                <p className="font-semibold text-gray-900 text-sm mb-0.5">
                  {order.property?.listingTitle || 'Property Name'}
                </p>
                <p className="text-xs text-gray-600">
                  {formatAddress(order.property?.address)}
                </p>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Delivery Address</h4>
              <div className="bg-gradient-to-br from-gray-50/80 to-gray-100/60 backdrop-blur-sm rounded-xl p-3 border border-gray-200/50">
                <p className="text-xs text-gray-900">
                  {formatAddress(order.deliveryAddress) || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-3">
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Expected Delivery</h4>
              <div className="bg-gradient-to-br from-gray-50/80 to-gray-100/60 backdrop-blur-sm rounded-xl p-3 border border-gray-200/50">
                <p className="text-xs text-gray-900">{formatDate(order.inStockDate)}</p>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Commission</h4>
              <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 backdrop-blur-sm rounded-xl p-3 border border-green-200/50">
                <p className="text-xl font-bold text-green-600">${order.hostFee?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-green-700 font-medium">Earned</p>
              </div>
            </div>
          </div>
        </div>

        {/* Order Items */}
        {order.items && order.items.length > 0 && (
          <div className="mt-4">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Order Items</h4>
            <div className="space-y-1.5">
              {order.items.slice(0, 3).map((item, itemIndex) => (
                <div key={itemIndex} className="flex items-center justify-between bg-gradient-to-br from-gray-50/60 to-gray-100/40 backdrop-blur-sm rounded-lg p-2.5 border border-gray-200/30">
                  <div className="flex items-center space-x-2.5">
                    <div className="w-7 h-7 bg-gradient-to-br from-primary-500 to-purple-500 rounded-lg flex items-center justify-center shadow-sm">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-900">{item.name || 'Item'}</p>
                      <p className="text-xs text-gray-500">Qty: {item.quantity || 1}</p>
                    </div>
                  </div>
                  <p className="text-xs font-bold text-gray-900">${item.price?.toFixed(2) || '0.00'}</p>
                </div>
              ))}
              {order.items.length > 3 && (
                <p className="text-xs text-gray-500 text-center py-1">+{order.items.length - 3} more items</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Filter Component ────────────────────────────────────────────────────────
function OrderFilters({ search, setSearch, statusFilter, setStatusFilter, viewMode, setViewMode, orders }) {
  const statusOptions = [
    { value: 'all', label: 'All', icon: null, gradient: 'from-gray-500 to-gray-600' },
    { value: 'completed', label: 'Completed', icon: '✓', gradient: 'from-green-500 to-emerald-500' },
    { value: 'picked', label: 'Picked Up', icon: '📦', gradient: 'from-blue-500 to-indigo-500' },
    { value: 'pending', label: 'Upcoming', icon: '⏳', gradient: 'from-amber-500 to-yellow-500' },
    { value: 'requested', label: 'Requested', icon: '📝', gradient: 'from-orange-500 to-amber-500' },
    { value: 'cancelled', label: 'Cancelled', icon: '✕', gradient: 'from-red-500 to-rose-500' }
  ];

  // Count orders by status
  const getStatusCount = (status) => {
    if (!orders) return 0;
    if (status === 'all') return orders.length;
    return orders.filter(o => o.status === status).length;
  };

  const hasActiveFilters = search || statusFilter !== 'pending';

  return (
    <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/40 p-5 mb-6 space-y-4">
      {/* Top Row: Search + View Toggle + Clear */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        {/* Search */}
        <div className="flex-1">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Search Orders</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by ID, property, city..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block w-full pl-10 pr-10 py-2.5 border border-gray-200/60 rounded-xl leading-5 bg-white/80 backdrop-blur-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 text-sm transition-all duration-200"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* View Toggle */}
        <div className="sm:w-auto">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">View</label>
          <div className="flex items-center bg-gray-100/60 backdrop-blur-sm rounded-xl p-1 border border-gray-200/40">
            <button
              onClick={() => setViewMode('card')}
              title="Card view"
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${viewMode === 'card' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              <span className="hidden sm:inline">Cards</span>
            </button>
            <button
              onClick={() => setViewMode('table')}
              title="List view"
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${viewMode === 'table' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              <span className="hidden sm:inline">List</span>
            </button>
          </div>
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <div className="sm:w-auto">
            <label className="block text-xs font-semibold text-transparent mb-1.5">Clear</label>
            <button
              onClick={() => { setSearch(''); setStatusFilter('all'); }}
              className="flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 bg-gray-100/60 hover:bg-gray-200/60 rounded-xl transition-all duration-200 border border-gray-200/40"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Reset</span>
            </button>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200/50" />

      {/* Bottom Row: Status Pills */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2.5">Filter by Status</label>
        <div className="flex flex-wrap gap-2">
          {statusOptions.map((option) => {
            const count = getStatusCount(option.value);
            const isActive = statusFilter === option.value;
            
            return (
              <button
                key={option.value}
                onClick={() => setStatusFilter(option.value)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium transition-all duration-200 border ${
                  isActive
                    ? `bg-gradient-to-r ${option.gradient} text-white border-transparent shadow-md`
                    : 'bg-white/80 text-gray-700 border-gray-200/60 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <span>{option.label}</span>
                <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold ${
                  isActive
                    ? 'bg-white/25 text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Stats Cards ─────────────────────────────────────────────────────────────
function OrderStats({ orders }) {
  const stats = {
    total: orders?.length || 0,
    completed: orders?.filter(o => o.status === 'completed').length || 0,
    pending: orders?.filter(o => ['pending', 'requested'].includes(o.status)).length || 0,
    totalEarnings: orders?.reduce((sum, order) => sum + (order.hostFee || 0), 0) || 0
  };

  const statCards = [
    {
      label: 'Total Orders',
      value: stats.total,
      gradient: 'from-blue-500 to-indigo-600',
      shadowColor: 'shadow-blue-500/25',
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
      ),
    },
    {
      label: 'Completed',
      value: stats.completed,
      gradient: 'from-green-500 to-emerald-600',
      shadowColor: 'shadow-green-500/25',
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      ),
    },
    {
      label: 'Pending',
      value: stats.pending,
      gradient: 'from-amber-500 to-orange-600',
      shadowColor: 'shadow-amber-500/25',
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      ),
    },
    {
      label: 'Total Earnings',
      value: `$${stats.totalEarnings.toFixed(2)}`,
      gradient: 'from-emerald-500 to-green-600',
      shadowColor: 'shadow-emerald-500/25',
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
      ),
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
      {statCards.map((stat, i) => (
        <div
          key={i}
          className="group bg-white/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/40 p-5 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
        >
          <div className="flex items-center">
            <div className={`w-11 h-11 bg-gradient-to-br ${stat.gradient} rounded-xl flex items-center justify-center shadow-lg ${stat.shadowColor} group-hover:scale-110 transition-transform duration-300`}>
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {stat.icon}
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{stat.label}</p>
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Pagination Component ────────────────────────────────────────────────────
function Pagination({ currentPage, totalPages, totalItems, pageSize, onPageChange, onPageSizeChange }) {
  const pageStart = (currentPage - 1) * pageSize + 1;
  const pageEnd = Math.min(currentPage * pageSize, totalItems);

  const getVisiblePages = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  if (totalItems === 0) return null;

  return (
    <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/40 p-4 mt-6">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        {/* Results Info */}
        <div className="flex items-center space-x-4">
          <p className="text-sm text-gray-600">
            Showing <span className="font-bold text-gray-900">{pageStart}</span> to{' '}
            <span className="font-bold text-gray-900">{pageEnd}</span> of{' '}
            <span className="font-bold text-gray-900">{totalItems}</span> orders
          </p>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Per page:</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="px-2 py-1 border border-gray-200/60 rounded-lg bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30"
            >
              {[10, 25, 50].map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Page Buttons */}
        <div className="flex items-center space-x-1">
          <button
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            className="px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {getVisiblePages().map((page) => (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`w-9 h-9 rounded-xl text-sm font-semibold transition-all duration-200 ${
                page === currentPage
                  ? 'bg-gradient-to-r from-primary-500 to-purple-600 text-white shadow-lg shadow-primary-500/30'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Orders Screen ──────────────────────────────────────────────────────
const OrdersScreen = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { loading, orders, error } = useSelector((state) => state.orders)
  const { hostInfo: { _id: hid } } = useSelector((state) => state.host)

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('pending')
  const [viewMode, setViewMode] = useState('card')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // Filter orders
  const filteredOrders = useMemo(() => {
    return orders?.filter((order) => {
      const matchesSearch = !search ||
        (order.uniqueId && order.uniqueId.toLowerCase().includes(search.toLowerCase())) ||
        (order._id && order._id.toLowerCase().includes(search.toLowerCase())) ||
        (order.status && order.status.toLowerCase().includes(search.toLowerCase())) ||
        (order.property?.address?.line1 && order.property.address.line1.toLowerCase().includes(search.toLowerCase())) ||
        (order.property?.address?.city && order.property.address.city.toLowerCase().includes(search.toLowerCase()))

      const matchesStatus = statusFilter === 'all' || order.status === statusFilter
      return matchesSearch && matchesStatus
    }) || []
  }, [orders, search, statusFilter])

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / pageSize)
  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredOrders.slice(start, start + pageSize)
  }, [filteredOrders, currentPage, pageSize])

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [search, statusFilter, pageSize])

  useEffect(() => {
    if (hid) {
      dispatch(getAllOrders(hid))
    }
  }, [dispatch, hid])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 flex items-center justify-center">
        <div className="text-center">
          <CircularProgress size={60} sx={{ color: '#EF4949' }} />
          <p className="mt-4 text-gray-600 font-semibold">Loading orders...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100 relative overflow-hidden">
      {/* Subtle Animated Background Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-gradient-to-br from-primary-500/5 to-purple-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] bg-gradient-to-tr from-blue-500/5 to-primary-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/3 right-1/4 w-[300px] h-[300px] bg-gradient-to-r from-purple-500/3 to-pink-500/3 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <HostNav hide={true} />

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Orders</h1>
              <p className="text-gray-500 mt-1">Manage and track your property orders</p>
            </div>
            <Link
              to="/host"
              className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-lg shadow-primary-500/25 hover:shadow-xl hover:-translate-y-0.5 font-medium text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Back to Dashboard</span>
            </Link>
          </div>
        </div>

        {/* Stats */}
        <OrderStats orders={orders} />

        {/* Filters */}
        <OrderFilters
          search={search}
          setSearch={setSearch}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          viewMode={viewMode}
          setViewMode={setViewMode}
          orders={orders}
        />

        {/* Error */}
        {error && (
          <div className="bg-red-50/80 backdrop-blur-sm border border-red-200/60 rounded-xl p-4 mb-6">
            <p className="text-red-800 font-medium">Error loading orders: {error}</p>
          </div>
        )}

        {/* Orders List */}
        {filteredOrders.length === 0 ? (
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/40 p-12 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-gray-300/30">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {search || statusFilter !== 'all' ? 'No Orders Found' : 'No Orders Yet'}
            </h3>
            <p className="text-gray-500 mb-6">
              {search || statusFilter !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'Orders will appear here when guests make purchases'
              }
            </p>
            {(search || statusFilter !== 'all') && (
              <button
                onClick={() => { setSearch(''); setStatusFilter('all'); }}
                className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-3 rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-300 font-medium shadow-lg shadow-primary-500/25"
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : viewMode === 'table' ? (
          /* ─── Table View ─────────────────────────────────────────────── */
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/40 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200/50">
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Order ID</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Property</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Commission</th>
                    <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Items</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100/50">
                  {paginatedOrders.map((order, index) => (
                    <OrderTableRow
                      key={order._id || index}
                      order={order}
                      index={(currentPage - 1) * pageSize + index}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* ─── Card View ──────────────────────────────────────────────── */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {paginatedOrders.map((order, index) => (
              <OrderCard key={order._id || index} order={order} index={(currentPage - 1) * pageSize + index} />
            ))}
          </div>
        )}

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filteredOrders.length}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      </div>
    </div>
  )
}

export default OrdersScreen
